import axios from "axios";

export default class WebserviceCore {
	/**
	 *
	 * @param baseUrl (optional) baseurl to add to front of all calls
	 * @param ajaxStatusCore AjaxStatusCore (optional) engine for recording when ajax start/stopped
	 * @param csrfName (optional) CSRF name - for posting
	 * @param csrfToken (optional) CSRF token - for posting
	 * @param jsLogging (optional) jsLogging object if using jsLogging
	 * @deprecated @param allResultsCallback (optional) (data) => {whatever you want ; return whatever you want to continue to pass through to next next of promise}; allows providing a common handler for all responses
	 * @param loadDefaultsCallback (optional) (axios.defaults) => useful for setting headers or other deafult information for axios calls: https://github.com/axios/axios => Config Defaults
	 * @param rawPromiseCallback (optional) (function) => get the axios promise (note that data is in result.data) as this gives the full axios response data; this promise has no other handlers called on it yet, so you will get the raw promise results; great for handling http exceptions; return the promise from the callback
	 * @param handledPromiseCallback (optional) (function) => disseminated promise data after generic catch and then handling
	 */
	constructor({
				baseUrl,
				ajaxStatusCore,
				csrfName,
				csrfToken,
				jsLogging,
				loadDefaultsCallback,
				rawPromiseCallback,
				// deprecated - use rawPromiseCallback and/or handledPromiseCallback instead
				allResultsCallback,
				handledPromiseCallback,
			} = {}) {
		this.data = {
			baseUrl: baseUrl ? baseUrl : '',
			ajaxStatusCore: ajaxStatusCore,
			csrfName: csrfName,
			csrfToken: csrfToken,
			jsLogging: jsLogging,
			allResultsCallback: allResultsCallback,
			loadDefaultsCallback: loadDefaultsCallback,
			rawPromiseCallback: rawPromiseCallback,
			handledPromiseCallback: handledPromiseCallback,
		};

		if (!!this.data.csrfName !== !!this.data.csrfToken) {
			console.error('Both csrf fields must be supplied if either is used', [this.data.csrfName, this.data.csrfToken]);
		}
	}

	// == Ajax calls == //
	requestWithData(verb, url, data, ajaxId) {
		this.data.loadDefaultsCallback && this.data.loadDefaultsCallback(axios.defaults);
		this.ajaxStateChange(ajaxId, true);
		const comboUrl = `${this.data.baseUrl}${url}`;
		return this.catchPromise(ajaxId, axios[verb](this.csrfUrl(comboUrl), this.csrf(data)));
	}
	post(url, data, ajaxId) {
		return this.requestWithData('post', url, data, ajaxId);
	}

	/**
	 * post data including files
	 *
	 * @param url destination endpoint
	 * @param files a single file or a FileList of files to post to the url
	 * @param additionalData can add additional fields to post with the file data
	 * @param ajaxId ID for knowing when this particular request is running
	 * @param fileFieldName field name in the form to use for the file(s)
	 * @return Promise
	 */
	postFile(url, files, ajaxId, additionalData = undefined, fileFieldName = 'file') {
		!ajaxId && console.error(`Should have an ajaxId for ajax call: ${url}`);

		this.data.loadDefaultsCallback && this.data.loadDefaultsCallback(axios.defaults);
		this.ajaxStateChange(ajaxId, true);

		const formData = new FormData();
		const filesArray = Array.from(files);
		const fieldName = `${fileFieldName}${filesArray.length > 1 ? '[]' : ''}`;
		filesArray.forEach(file => formData.append(fieldName, file));

		if (additionalData) {
			_.map(additionalData,(key, value) => formData.append(key, value));
		}

		if (this.data.csrfName) {
			formData.append(this.data.csrfName, this.data.csrfToken);
		}

		const comboUrl = `${this.data.baseUrl}${url}`;
		return this.catchPromise(ajaxId, axios.post(this.csrfUrl(comboUrl), formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
	}
	put(url, data, ajaxId) {
		return this.requestWithData('put', url, data, ajaxId);
	}
	patch(url, data, ajaxId) {
		return this.requestWithData('patch', url, data, ajaxId);
	}

	requestWithUrlData(verb, url, data, ajaxId) {
		this.data.loadDefaultsCallback && this.data.loadDefaultsCallback(axios.defaults);
		this.ajaxStateChange(ajaxId, true);
		const comboUrl = `${this.data.baseUrl}${url}`;
		return this.catchPromise(ajaxId, axios[verb](this.csrfUrl(data ? Object.keys(data).reduce((urlUse, key) => this.addToUrl(urlUse, key, data[key]), comboUrl) : comboUrl)));
	}
	get(url, data, ajaxId) {
		return this.requestWithUrlData('get', url, data, ajaxId);
	}
	delete(url, data, ajaxId) {
		return this.requestWithUrlData('delete', url, data, ajaxId);
	}
	head(url, data, ajaxId) {
		return this.requestWithUrlData('head', url, data, ajaxId);
	}
	options(url, data, ajaxId) {
		return this.requestWithUrlData('options', url, data, ajaxId);
	}

	/**
	 * before the endpoint is available, the webservice can "mock" a call
	 * the mocked call will return back whatever you pass to it
	 * the mocked call will be easily replaced by get/post or whatever verb is needed
	 *
	 * @param url
	 * @param data
	 * @param ajaxId
	 * @param returnData
	 * @return {Promise<any>}
	 */
	mock(url, data, ajaxId, returnData) {
		console.error('Mocked Call', url, data, ajaxId, returnData);
		return Promise.resolve(returnData);
	}


	// internal methods, don't worry about this
	ajaxStateChange(ajaxId, ajaxStarted) {
		this.data.ajaxStatusCore && this.data.ajaxStatusCore.changeAjaxing(ajaxId, ajaxStarted);
	}

	catchPromise(ajaxId, promise) {
		// provide raw access to the promise
		let resultPromise = this.data.rawPromiseCallback ? this.data.rawPromiseCallback(promise) : promise;

		// do global handling and return just data from axios result
		resultPromise = resultPromise
		// turn off ajax spinner; catch first in case there was an error
			.catch(error => {
				this.ajaxStateChange((ajaxId, false));
				throw error;
			})
			// turn off ajax spinner; then after catch so if there wasn't an error (don't want to turn off ajax twice)
			.then(result => {
				this.ajaxStateChange(ajaxId, false);
				return result.data;
			})

			// allow webservice core owner a crack at all data coming back
			.then(result => this.data.allResultsCallback ? this.data.allResultsCallback(result) : result)

			// handle any errors up to this point
			.catch(error => {
				console.error(error);
				this.data.jsLogging && this.data.jsLogging.log(error);

				// propagate the error
				throw error;
			});

		// allow global handling after the promise is disseminated
		resultPromise = this.data.handledPromiseCallback ? this.data.handledPromiseCallback(resultPromise) : resultPromise;

		return resultPromise;
	}

	/**
	 * adds csrf to data packet. useful for ajax calls.
	 * @param data your data to which the csrf name/token will be added. can be false/null/blank
	 */
	csrf(data) {
		return this.data.csrfName ? Object.assign({[this.data.csrfName]: this.data.csrfToken}, data) : data;
	}

	/**
	 * add csrf token to url
	 *
	 * @param url the url
	 * @return {string} url w/ token
	 */
	csrfUrl(url) {
		return this.data.csrfName ? this.addToUrl(`${url}`, this.data.csrfName, this.data.csrfToken) : url;
	}

	addToUrl(url, key, value) {
		return `${url}${url.includes('?') ? '&' : '?'}${key}=${value}`;
	}
}
